import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {checkinBooking} from '@/api/checkin/index';
import PhoneInput from 'react-phone-number-input';
import Footer from '@components/Footer';
import {useTranslation} from 'react-i18next';
import {getHomeUrl} from '@/utils/getHomeUrl';

interface CustomerLookupInfo {
  phone?: string;
  email?: string;
  birth?: string;
}

export default function CreateChartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {i18n, t} = useTranslation();
  const customerLookupInfo = location.state as CustomerLookupInfo | null;
  const initialBirth = customerLookupInfo?.birth?.split('-') || [];
  const [language, setLanguage] = useState(i18n.language);

  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string | undefined>(
    customerLookupInfo?.phone || '',
  );
  const [email, setEmail] = useState<string>(customerLookupInfo?.email || '');
  const [birthYear, setBirthYear] = useState(initialBirth[0] || '');
  const [birthMonth, setBirthMonth] = useState(
    initialBirth[1] ? String(Number(initialBirth[1])) : '',
  );
  const [birthDay, setBirthDay] = useState(
    initialBirth[2] ? String(Number(initialBirth[2])) : '',
  );
  const birth =
    birthYear && birthMonth && birthDay
      ? `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`
      : '';
  const [gender, setGender] = useState<'male' | 'female' | ''>('');

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const {mutate: bookingMutate} = checkinBooking();

  const handleCreateChart = () => {
    if (
      !name ||
      !phone ||
      !email ||
      !birthYear ||
      !birthMonth ||
      !birthDay ||
      !gender
    ) {
      setErrorMsg(t('createChart.error_message.invalid_form'));
      return;
    }

    setIsLoading(true);
    setErrorMsg('');

    const new_customer = {
      name,
      phone,
      email,
      birth,
      gender: gender == 'female' ? true : false,
      language: language,
    };
    bookingMutate(
      {new_customer, walk_in_available: true},
      {
        onSuccess: () => {
          setShowSuccessDialog(true);
        },
        onError: error => {
          setErrorMsg(t('createChart.error_message.checkin'));
        },
        onSettled: () => {
          setIsLoading(false);
        },
      },
    );
  };

  return (
    <div className='bg-white min-h-screen'>
      <div className='max-w-lg mx-auto px-4 pt-16'>
        <h2 className='text-xl font-bold mb-6'>{t('createChart.title')}</h2>

        <div className='space-y-4'>
          <div>
            <label htmlFor='name' className='block mb-1 font-medium'>
              {t('createChart.form.name.title')}{' '}
              <span className='text-red-500'>*</span>
            </label>
            <input
              id='name'
              type='text'
              placeholder={t('createChart.form.name.placeholder')}
              className='w-full border border-gray-300 rounded px-3 py-2'
              value={name}
              onChange={e => setName(e.target.value)}
            />
            <p className='text-sm text-gray-600 mt-1'>
              {t('createChart.form.name.passport_notice')}
            </p>
          </div>

          <div>
            <label htmlFor='phone' className='block mb-1 font-medium'>
              {t('createChart.form.phone.title')}{' '}
              <span className='text-red-500'>*</span>
            </label>
            <PhoneInput
              placeholder={t('createChart.form.phone.placeholder')}
              defaultCountry='KR'
              value={phone}
              onChange={value => setPhone(value)}
              className='w-full border border-gray-300 rounded px-3 py-2 focus:outline-none'
            />
          </div>

          <div>
            <label htmlFor='email' className='block mb-1 font-medium'>
              {t('createChart.form.email.title')}{' '}
              <span className='text-red-500'>*</span>
            </label>
            <input
              id='email'
              type='email'
              placeholder={t('createChart.form.email.placeholder')}
              className='w-full border border-gray-300 rounded px-3 py-2'
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor='birth' className='block mb-1 font-medium'>
              {t('createChart.form.birth.title')}{' '}
              <span className='text-red-500'>*</span>
            </label>
            <div className='flex gap-2'>
              {/* 연도 */}
              <select
                value={birthYear}
                onChange={e => setBirthYear(e.target.value)}
                className='flex-1 border border-gray-300 rounded px-3 py-2'>
                <option value=''>{t('createChart.form.birth.year')}</option>
                {[...Array(100)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  );
                })}
              </select>
              {/* 월 */}
              <select
                value={birthMonth}
                onChange={e => setBirthMonth(e.target.value)}
                className='w-32 border border-gray-300 rounded px-3 py-2'>
                <option value=''>{t('createChart.form.birth.month')}</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={String(i + 1)}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {/* 일 */}
              <select
                value={birthDay}
                onChange={e => setBirthDay(e.target.value)}
                className='w-32 border border-gray-300 rounded px-3 py-2'>
                <option value=''>{t('createChart.form.birth.day')}</option>
                {[...Array(31)].map((_, i) => (
                  <option key={i + 1} value={String(i + 1)}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className='block mb-1 font-medium'>
              {t('createChart.form.gender.title')}{' '}
              <span className='text-red-500'>*</span>
            </label>
            <div className='flex gap-4'>
              <label className='flex items-center'>
                <input
                  type='radio'
                  name='gender'
                  value='male'
                  checked={gender === 'male'}
                  onChange={() => setGender('male')}
                  className='mr-2'
                />
                {t('createChart.form.gender.male')}
              </label>
              <label className='flex items-center'>
                <input
                  type='radio'
                  name='gender'
                  value='female'
                  checked={gender === 'female'}
                  onChange={() => setGender('female')}
                  className='mr-2'
                />
                {t('createChart.form.gender.female')}
              </label>
            </div>
          </div>

          {errorMsg && <div className='text-red-500 text-sm'>{errorMsg}</div>}

          <button
            onClick={handleCreateChart}
            className='w-full bg-black text-white py-4 rounded mt-4'
            disabled={isLoading}>
            {isLoading
              ? t('createChart.form.button.loading')
              : t('createChart.form.button.title')}
          </button>
        </div>

        {showSuccessDialog && (
          <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-70'>
            <div className='bg-white p-6 rounded shadow-lg w-80 text-center'>
              <p className='text-lg font-bold whitespace-pre-line'>
                {t('createChart.dialog_message.complete.title')}
              </p>
              <button
                className='w-full bg-black text-white py-3 mt-4 rounded'
                onClick={() => navigate(getHomeUrl())}>
                {t('createChart.dialog_message.complete.button_title')}
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
